import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  checklistItemsRepo,
  checklistTemplatesRepo,
  countriesRepo,
  dealPartiesRepo,
  dealsRepo,
  documentsRepo,
  incotermsRepo,
  isSupabaseConfigured,
  partnersRepo,
  paymentTermsRepo,
  productsRepo,
  shipperProfilesRepo,
  workflowStagesRepo,
} from "@/lib/db";
import { gate } from "@/lib/rules";
import { generateInvoicePacking, generateProformaInvoice } from "@/lib/merge";
import {
  renderCepaCoWorksheet,
  renderDealReport,
  renderExportDeclarationSheet,
  renderGeneralCoWorksheet,
} from "@/lib/report";
import type {
  ChecklistItem,
  ChecklistTemplate,
  Country,
  Deal,
  DealLineItem,
  DealParty,
  DealStatus,
  DocumentRecord,
  DocumentType,
  Incoterm,
  Partner,
  PaymentTerm,
  Product,
  ShipperProfile,
  WorkflowStage,
} from "@/types";
import { ConfigBanner } from "@/components/ConfigBanner";
import { Disclaimer } from "@/components/Disclaimer";
import { FindingsTable, GateSummary } from "@/components/FindingsTable";
import { buildRuleContext } from "./buildContext";

/** deals.status progression (single source of truth; cancelled excluded). */
const STATUS_ORDER: DealStatus[] = [
  "quote",
  "order",
  "ship_prep",
  "doc_issued",
  "customs_shipping",
  "done",
];

const STATUS_LABEL: Record<DealStatus, string> = {
  quote: "견적",
  order: "주문",
  ship_prep: "선적준비",
  doc_issued: "서류발행",
  customs_shipping: "통관/발송",
  done: "완료",
  cancelled: "취소",
};

const OWNER_LABEL: Record<WorkflowStage["owner"], string> = {
  exporter: "수출자",
  forwarder: "포워더",
  broker: "관세사",
  bank: "은행",
  importer: "수입자",
  mixed: "공동",
};

const ROLE_LABEL: Record<string, string> = {
  authorized_agent: "인도 공인대리인 (Authorized Agent)",
  lar: "대만 현지대리인 (LAR)",
};

/** Legacy roles resolved from deals FK columns — not assigned via deal_parties. */
const LEGACY_ROLES = new Set(["importer_of_record", "fda_initial_importer"]);

const DOC_LABEL: Partial<Record<DocumentType, string>> = {
  proforma_invoice: "PI (견적송장)",
  commercial_invoice: "CI (상업송장)",
  packing_list: "PL (포장명세서)",
  cepa_co_worksheet: "CEPA CO 신청 워크시트",
  general_co_worksheet: "일반 CO 신청 워크시트",
  export_declaration_sheet: "수출신고 정보 시트",
  deal_report: "딜 보고서",
};

/** Entrance ② — 거래 상세 워크스페이스 (/deals/:id). v2: 10단계 워크플로우 뷰. */
export function DealWorkspacePage(): JSX.Element {
  const { id = "" } = useParams();
  return <DealWorkspace dealId={id} />;
}

/**
 * v3 (unified-trade-flow FR-9) — 페이지에서 추출한 워크스페이스 본체.
 * 로직 불변: dealId 를 prop 으로 받아 /deals/:id 와 /flow 인라인 임베드가 공유.
 * embedded=true 면 페이지 전용 요소(딜 목록 백링크)만 숨긴다.
 */
export function DealWorkspace({
  dealId,
  embedded = false,
  onChanged,
}: {
  dealId: string;
  embedded?: boolean;
  /** 딜 상태/서류가 바뀔 때 호출 — 부모(/flow)가 목록을 재조회해 실적 롤업을 갱신한다. */
  onChanged?: () => void;
}): JSX.Element {
  const id = dealId;

  const [deal, setDeal] = useState<Deal | null>(null);
  const [lines, setLines] = useState<DealLineItem[]>([]);
  const [country, setCountry] = useState<Country | null>(null);
  const [incoterm, setIncoterm] = useState<Incoterm | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [shipper, setShipper] = useState<ShipperProfile | null>(null);
  const [paymentTerm, setPaymentTerm] = useState<PaymentTerm | null>(null);
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [dealParties, setDealParties] = useState<DealParty[]>([]);
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);

  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acked, setAcked] = useState<ReadonlySet<string>>(new Set());
  const [merging, setMerging] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // external refs form (deal columns)
  const [extRefs, setExtRefs] = useState({
    export_declaration_no: "",
    transport_doc_no: "",
    insurance_policy_no: "",
    lc_expiry_date: "",
    lc_latest_shipment_date: "",
  });

  const load = useCallback(async () => {
    if (!isSupabaseConfigured() || !id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await dealsRepo.get(id);
      if (!d) throw new Error("딜을 찾을 수 없습니다.");
      const [ls, cs, ics, ps, prods, shippers, pts, wss, dps, docs] =
        await Promise.all([
          dealsRepo.lineItems(id),
          countriesRepo.list(),
          incotermsRepo.list(),
          partnersRepo.list(),
          productsRepo.list(),
          shipperProfilesRepo.list(),
          paymentTermsRepo.list(),
          workflowStagesRepo.list(),
          dealPartiesRepo.listByDeal(id),
          documentsRepo.list(id),
        ]);
      setDeal(d);
      setLines(ls);
      setCountry(cs.find((c) => c.id === d.destination_country) ?? null);
      setIncoterm(ics.find((i) => i.id === d.incoterm_id) ?? null);
      setPartners(ps);
      setProducts(prods);
      setShipper(shippers.find((s) => s.id === d.shipper_profile_id) ?? null);
      setPaymentTerm(pts.find((p) => p.id === d.payment_term_id) ?? null);
      setStages(wss);
      setDealParties(dps);
      setDocuments(docs);
      setExtRefs({
        export_declaration_no: d.export_declaration_no ?? "",
        transport_doc_no: d.transport_doc_no ?? "",
        insurance_policy_no: d.insurance_policy_no ?? "",
        lc_expiry_date: d.lc_expiry_date ?? "",
        lc_latest_shipment_date: d.lc_latest_shipment_date ?? "",
      });
      // checklist: instantiate per-deal items from the country templates (lazy).
      const tpls = await checklistTemplatesRepo.listByCountry(d.destination_country);
      setTemplates(tpls);
      setChecklist(await checklistItemsRepo.ensureForDeal(id, tpls));
      // default selected stage = first stage mapped to the current status.
      setSelectedStage((prev) => {
        if (prev) return prev;
        const cur = wss.find((s) => s.deal_status === d.status);
        return cur?.stage_key ?? wss[0]?.stage_key ?? null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const today = new Date().toISOString().slice(0, 10);

  const ctx = useMemo(() => {
    if (!deal) return null;
    return buildRuleContext({
      deal,
      lines,
      country,
      incoterm,
      partners,
      products,
      dealParties,
      documents,
      shipper,
      paymentTerm,
      today,
    });
  }, [deal, lines, country, incoterm, partners, products, dealParties, documents, shipper, paymentTerm, today]);

  const result = useMemo(() => (ctx ? gate(ctx, acked) : null), [ctx, acked]);

  const openErrorRuleIds = useMemo(
    () =>
      result
        ? [
            ...new Set(
              result.findings
                .filter((f) => f.severity === "ERROR" && f.status === "open")
                .map((f) => f.rule_id),
            ),
          ]
        : [],
    [result],
  );

  function toggleAck(ruleId: string) {
    setAcked((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  }

  const stage = stages.find((s) => s.stage_key === selectedStage) ?? null;
  const stageChecklist = useMemo(() => {
    if (!stage) return [];
    const keys = new Set(
      templates.filter((t) => t.stage_key === stage.stage_key).map((t) => t.item_key),
    );
    return checklist.filter((i) => keys.has(i.item_key));
  }, [stage, templates, checklist]);

  /** Roles the destination requires that are assigned via deal_parties (non-legacy). */
  const assignableRoles = (country?.required_importer_roles ?? []).filter(
    (r) => !LEGACY_ROLES.has(r),
  );
  const partyByRole = new Map(dealParties.map((p) => [p.role, p]));

  async function assignParty(role: string, partnerId: string) {
    if (!deal) return;
    try {
      if (partnerId) await dealPartiesRepo.set(deal.id, role, partnerId);
      else await dealPartiesRepo.remove(deal.id, role);
      setDealParties(await dealPartiesRepo.listByDeal(deal.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleChecklist(item: ChecklistItem) {
    const next = item.status === "done" ? "pending" : "done";
    try {
      await checklistItemsRepo.setStatus(item.id, next);
      setChecklist((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: next } : i)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function saveExtRefs() {
    if (!deal) return;
    try {
      await dealsRepo.update(deal.id, {
        export_declaration_no: extRefs.export_declaration_no || null,
        transport_doc_no: extRefs.transport_doc_no || null,
        insurance_policy_no: extRefs.insurance_policy_no || null,
        lc_expiry_date: extRefs.lc_expiry_date || null,
        lc_latest_shipment_date: extRefs.lc_latest_shipment_date || null,
      });
      setSavedMsg("외부 서류 참조가 저장되었습니다.");
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function advanceStatus() {
    if (!deal) return;
    const idx = STATUS_ORDER.indexOf(deal.status);
    const next = STATUS_ORDER[idx + 1];
    if (!next) return;
    try {
      await dealsRepo.update(deal.id, { status: next });
      setSelectedStage(null); // re-resolve to the new status's first stage
      void load();
      onChanged?.(); // 부모 재조회 → 완료 딜이 실적 '확정 대기'에 즉시 반영
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function generateCiPl() {
    if (!ctx || !deal) return;
    setMerging(true);
    setSavedMsg(null);
    setError(null);
    try {
      const buf = await generateInvoicePacking(ctx, { shipper });
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CI-PL-${deal.deal_no}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

      const findingsSnapshot = {
        passed: result?.passed ?? false,
        blocks: result?.blocks ?? 0,
        errors: result?.errors ?? 0,
        findings: (result?.findings ?? []).map((f) => ({
          rule_id: f.rule_id,
          severity: f.severity,
          status: f.status,
        })),
      };
      const payload = {
        deal,
        lines,
        shipper,
        incoterm,
        country,
        issued_at: new Date().toISOString(),
      };
      for (const docType of ["commercial_invoice", "packing_list"] as const) {
        const doc = await documentsRepo.create({
          deal_id: deal.id,
          doc_type: docType,
          template_id: null,
          doc_no: `${docType === "commercial_invoice" ? "CI" : "PL"}-${deal.deal_no}`,
          current_version: 1,
          status: "issued",
        });
        await documentsRepo.saveVersion({
          document_id: doc.id,
          version: 1,
          payload_json: payload,
          rendered_file_ref: null,
          rendered_format: "xlsx",
          findings_snapshot: findingsSnapshot,
          issued_by: "mmakid",
          revision_reason: null,
        });
      }
      try {
        if (STATUS_ORDER.indexOf(deal.status) < STATUS_ORDER.indexOf("doc_issued")) {
          await dealsRepo.update(deal.id, { status: "doc_issued" });
        }
      } catch {
        /* status advance is best-effort; download already succeeded */
      }
      setSavedMsg("CI + PL 생성·다운로드 완료. 발행본 스냅샷이 저장되었습니다.");
      void load();
      onChanged?.(); // 서류발행 상태 변경 → 부모 재조회
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMerging(false);
    }
  }

  /** Persist an issued document + immutable version snapshot (DP-5). */
  async function persistDoc(
    docType: DocumentType,
    docNo: string,
    renderedFormat: "xlsx" | "html",
  ) {
    if (!deal) return;
    const findingsSnapshot = {
      passed: result?.passed ?? false,
      blocks: result?.blocks ?? 0,
      errors: result?.errors ?? 0,
      findings: (result?.findings ?? []).map((f) => ({
        rule_id: f.rule_id,
        severity: f.severity,
        status: f.status,
      })),
    };
    const doc = await documentsRepo.create({
      deal_id: deal.id,
      doc_type: docType,
      template_id: null,
      doc_no: docNo,
      current_version: 1,
      status: "issued",
    });
    await documentsRepo.saveVersion({
      document_id: doc.id,
      version: 1,
      payload_json: {
        deal,
        lines,
        shipper,
        incoterm,
        country,
        issued_at: new Date().toISOString(),
      },
      rendered_file_ref: null,
      rendered_format: renderedFormat,
      findings_snapshot: findingsSnapshot,
      issued_by: "mmakid",
      revision_reason: null,
    });
  }

  /** PI — quote-stage document; not gate-blocked (importer roles may be TBD at quote). */
  async function generatePi() {
    if (!ctx || !deal) return;
    setMerging(true);
    setSavedMsg(null);
    setError(null);
    try {
      const buf = await generateProformaInvoice(ctx, { shipper });
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PI-${deal.deal_no}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      await persistDoc("proforma_invoice", `PI-${deal.deal_no}`, "xlsx");
      setSavedMsg("PI 생성·다운로드 완료. 발행본 스냅샷이 저장되었습니다.");
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMerging(false);
    }
  }

  /** Worksheets & report — open a printable HTML view + snapshot it. */
  async function issueHtmlDoc(docType: DocumentType) {
    if (!ctx || !deal) return;
    setSavedMsg(null);
    setError(null);
    try {
      const ciDocNo =
        documents.find((d) => d.doc_type === "commercial_invoice")?.doc_no ?? null;
      const input = { ctx, shipper, paymentTerm, ciDocNo, issuedAt: today };
      let html = "";
      let prefix = "";
      if (docType === "cepa_co_worksheet") {
        html = renderCepaCoWorksheet(input);
        prefix = "COWS";
      } else if (docType === "general_co_worksheet") {
        html = renderGeneralCoWorksheet(input);
        prefix = "GCOWS";
      } else if (docType === "export_declaration_sheet") {
        html = renderExportDeclarationSheet(input);
        prefix = "EDS";
      } else if (docType === "deal_report") {
        html = renderDealReport({
          ctx,
          shipper,
          paymentTerm,
          gateResult: result,
          stages,
          documents,
          checklist,
          issuedAt: today,
        });
        prefix = "RPT";
      } else {
        return;
      }
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      await persistDoc(docType, `${prefix}-${deal.deal_no}`, "html");
      setSavedMsg(`${DOC_LABEL[docType] ?? docType} 생성 완료 (새 탭에서 인쇄/PDF 저장).`);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Stage-card document action. CI/PL은 게이트 통과 필수, PI·워크시트·보고서는 즉시 생성. */
  function docAction(
    docType: DocumentType,
  ): { label: string; run?: () => void; gated?: boolean } {
    const label = DOC_LABEL[docType] ?? docType;
    if (docType === "commercial_invoice" || docType === "packing_list") {
      return { label: "CI + PL 생성", run: () => void generateCiPl(), gated: true };
    }
    if (docType === "proforma_invoice") {
      return { label: "PI 생성", run: () => void generatePi() };
    }
    if (
      docType === "cepa_co_worksheet" ||
      docType === "general_co_worksheet" ||
      docType === "export_declaration_sheet" ||
      docType === "deal_report"
    ) {
      return { label: `${label} 생성`, run: () => void issueHtmlDoc(docType) };
    }
    return { label };
  }

  const nextStatus =
    deal && STATUS_ORDER.indexOf(deal.status) >= 0
      ? STATUS_ORDER[STATUS_ORDER.indexOf(deal.status) + 1] ?? null
      : null;

  const isLC = paymentTerm?.type === "LC";

  // Deduplicate the CI+PL combined action on the stage card.
  const stageDocActions = useMemo(() => {
    if (!stage) return [];
    const seen = new Set<string>();
    const actions: { key: string; label: string; run?: () => void; gated?: boolean }[] = [];
    for (const dt of stage.doc_types) {
      const a = docAction(dt);
      if (seen.has(a.label)) continue;
      seen.add(a.label);
      actions.push({ key: dt, ...a });
    }
    return actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, ctx, deal, documents, checklist, result]);

  return (
    <div>
      <Disclaimer />
      <section className="panel">
        {!embedded && (
          <p className="sub">
            <Link to="/deals">← 딜 목록</Link>
          </p>
        )}

        {!isSupabaseConfigured() && (
          <>
            <ConfigBanner />
            <p className="muted">
              Supabase 미설정 상태입니다. 딜 워크스페이스는 환경변수 설정 후
              동작합니다.
            </p>
          </>
        )}

        {error && (
          <p className="notice-error" role="alert">
            {error}
          </p>
        )}
        {loading && <p className="muted">불러오는 중…</p>}

        {deal && (
          <>
            <h2>{deal.title ?? deal.deal_no}</h2>
            <p className="sub">
              <code>{deal.deal_no}</code> · 목적국 {deal.destination_country} ·
              상태{" "}
              <span className="status-badge">{STATUS_LABEL[deal.status]}</span>{" "}
              · 인코텀즈 {deal.incoterm_id ?? "—"}{" "}
              {deal.incoterm_named_place ?? ""}
              {paymentTerm ? ` · 결제 ${paymentTerm.label}` : ""}
            </p>

            {/* ── 워크플로우 단계 네비게이션 (FR-B2) ─────────────────────── */}
            {stages.length > 0 && (
              <div className="stage-nav" role="tablist" aria-label="수출 단계">
                {stages.map((s) => {
                  const isCurrent = s.deal_status === deal.status;
                  const isSelected = s.stage_key === selectedStage;
                  return (
                    <button
                      key={s.stage_key}
                      role="tab"
                      aria-selected={isSelected}
                      className={
                        "stage-chip" +
                        (isCurrent ? " current" : "") +
                        (isSelected ? " selected" : "")
                      }
                      onClick={() => setSelectedStage(s.stage_key)}
                      title={s.description ?? undefined}
                    >
                      <span className="stage-no">{Math.round(s.sort / 10)}</span>
                      {s.label_ko}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="workspace">
              <div className="ws-main">
                {/* ── 선택 단계 액션 카드 ────────────────────────────────── */}
                {stage && (
                  <div className="stage-card">
                    <h3 className="section-title">
                      {Math.round(stage.sort / 10)}. {stage.label_ko}{" "}
                      <span className="badge INFO">{OWNER_LABEL[stage.owner]}</span>
                      {stage.deal_status === deal.status && (
                        <span className="badge WARN">현재 단계</span>
                      )}
                    </h3>
                    {stage.description && (
                      <p className="muted">{stage.description}</p>
                    )}

                    {stageDocActions.length > 0 && (
                      <p>
                        {stageDocActions.map((a) =>
                          a.run ? (
                            <button
                              key={a.key}
                              onClick={a.run}
                              disabled={merging || (a.gated && !result?.passed)}
                              title={
                                a.gated ? "발행 게이트 통과 필요" : undefined
                              }
                              style={{ marginRight: 8 }}
                            >
                              {merging ? "생성 중…" : a.label}
                            </button>
                          ) : (
                            <button key={a.key} disabled style={{ marginRight: 8 }}>
                              {a.label}
                            </button>
                          ),
                        )}
                      </p>
                    )}

                    {stageChecklist.length > 0 && (
                      <>
                        <p className="roles-label">이 단계 체크리스트</p>
                        {stageChecklist.map((item) => (
                          <label key={item.id} className="check-row">
                            <input
                              type="checkbox"
                              checked={item.status === "done"}
                              onChange={() => void toggleChecklist(item)}
                            />{" "}
                            {item.label}
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {/* ── 필수 수입자 역할 지정 (FR-A3) ─────────────────────── */}
                {assignableRoles.length > 0 && (
                  <div className="stage-card">
                    <h3 className="section-title">필수 수입자 역할</h3>
                    {assignableRoles.map((role) => {
                      const current = partyByRole.get(role);
                      return (
                        <div className="field" key={role}>
                          <span className="field-label">
                            {ROLE_LABEL[role] ?? role}
                          </span>
                          <select
                            value={current?.partner_id ?? ""}
                            onChange={(e) => void assignParty(role, e.target.value)}
                          >
                            <option value="">— 미지정 (발행 차단) —</option>
                            {partners.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.company_name} ({p.country})
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── 외부 서류 참조 (FR-C5) ────────────────────────────── */}
                <div className="stage-card">
                  <h3 className="section-title">외부 서류 참조 (번호 기록 = 확인)</h3>
                  <div className="form-grid">
                    <label className="field">
                      <span className="field-label">수출신고필증 신고번호</span>
                      <input
                        value={extRefs.export_declaration_no}
                        onChange={(e) =>
                          setExtRefs({ ...extRefs, export_declaration_no: e.target.value })
                        }
                        placeholder="예: 12345-26-123456X"
                      />
                    </label>
                    <label className="field">
                      <span className="field-label">B/L · AWB 번호</span>
                      <input
                        value={extRefs.transport_doc_no}
                        onChange={(e) =>
                          setExtRefs({ ...extRefs, transport_doc_no: e.target.value })
                        }
                      />
                    </label>
                    <label className="field">
                      <span className="field-label">적하보험 증권번호 (CIF·CIP 필수)</span>
                      <input
                        value={extRefs.insurance_policy_no}
                        onChange={(e) =>
                          setExtRefs({ ...extRefs, insurance_policy_no: e.target.value })
                        }
                      />
                    </label>
                    {isLC && (
                      <>
                        <label className="field">
                          <span className="field-label">L/C 유효기일</span>
                          <input
                            type="date"
                            value={extRefs.lc_expiry_date}
                            onChange={(e) =>
                              setExtRefs({ ...extRefs, lc_expiry_date: e.target.value })
                            }
                          />
                        </label>
                        <label className="field">
                          <span className="field-label">L/C 최종선적일</span>
                          <input
                            type="date"
                            value={extRefs.lc_latest_shipment_date}
                            onChange={(e) =>
                              setExtRefs({
                                ...extRefs,
                                lc_latest_shipment_date: e.target.value,
                              })
                            }
                          />
                        </label>
                      </>
                    )}
                  </div>
                  <p>
                    <button onClick={() => void saveExtRefs()}>저장</button>
                  </p>
                </div>

                {/* ── 라인아이템 ────────────────────────────────────────── */}
                <h3 className="section-title">라인아이템</h3>
                <table className="findings">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>품명</th>
                      <th>수량</th>
                      <th>단가</th>
                      <th>금액</th>
                      <th>순/총중량</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr>
                        <td colSpan={6} className="muted">
                          라인아이템이 없습니다.
                        </td>
                      </tr>
                    )}
                    {lines.map((l) => (
                      <tr key={l.id}>
                        <td>{l.line_no}</td>
                        <td>{l.description_en ?? "—"}</td>
                        <td className="num">{l.quantity}</td>
                        <td className="num">{l.unit_price}</td>
                        <td className="num">
                          {Math.round(l.quantity * l.unit_price * 100) / 100}
                        </td>
                        <td className="num">
                          {l.net_weight_kg ?? "—"} / {l.gross_weight_kg ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* ── 서류 현황 (FR-C6) ─────────────────────────────────── */}
                <h3 className="section-title">서류 현황</h3>
                <table className="findings">
                  <thead>
                    <tr>
                      <th>서류</th>
                      <th>번호</th>
                      <th>버전</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted">
                          발행된 서류가 없습니다.
                        </td>
                      </tr>
                    )}
                    {documents.map((d) => (
                      <tr key={d.id}>
                        <td>{DOC_LABEL[d.doc_type] ?? d.doc_type}</td>
                        <td>
                          <code>{d.doc_no ?? "—"}</code>
                        </td>
                        <td className="num">v{d.current_version}</td>
                        <td>{d.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ws-side">
                <h3 className="section-title">발행 게이트</h3>
                {result && (
                  <>
                    <GateSummary
                      blocks={result.blocks}
                      errors={result.errors}
                      warns={result.warns}
                      infos={result.infos}
                      passed={result.passed}
                    />
                    <FindingsTable findings={result.findings} />

                    {result.blocks > 0 && (
                      <p className="gate-block-notice">
                        BLOCK {result.blocks}건 — 발행 불가. BLOCK은 확인(ack)으로
                        해제할 수 없습니다(DP-7). 원인 데이터를 수정하세요.
                      </p>
                    )}

                    {openErrorRuleIds.length > 0 && (
                      <div className="ack-list">
                        <p className="ack-list-title">
                          발행 전 아래 ERROR 항목을 확인(ack)하세요:
                        </p>
                        {openErrorRuleIds.map((ruleId) => (
                          <label key={ruleId} className="ack-item">
                            <input
                              type="checkbox"
                              checked={acked.has(ruleId)}
                              onChange={() => toggleAck(ruleId)}
                            />{" "}
                            <code>{ruleId}</code> 확인함
                          </label>
                        ))}
                      </div>
                    )}

                    <div style={{ marginTop: 16 }}>
                      <button
                        onClick={() => void generateCiPl()}
                        disabled={merging || !result.passed}
                      >
                        {merging ? "생성 중…" : "CI + PL 생성"}
                      </button>
                      {!result.passed && (
                        <p className="muted" style={{ marginTop: 8 }}>
                          게이트 미통과 — 발행 차단됨(DP-7).
                        </p>
                      )}
                      {savedMsg && (
                        <p
                          className="muted"
                          style={{ marginTop: 8, color: "var(--ok)" }}
                        >
                          {savedMsg}
                        </p>
                      )}
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <button onClick={() => void issueHtmlDoc("deal_report")}>
                        딜 보고서 생성
                      </button>
                      {nextStatus && (
                        <button
                          onClick={() => void advanceStatus()}
                          style={{ marginLeft: 8 }}
                        >
                          다음 단계로 → {STATUS_LABEL[nextStatus]}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
